/**
 * Explore store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { exploreApi } from '@/lib/api/explore';
import { pickMusicGradientMeta } from '@/constants/Colors';


// ──────────────────────────────────────────────
// Explore Store
// ──────────────────────────────────────────────

// Stamp a deterministic `gradient` (and a single `color` for places that only
// want one swatch) onto an explore item. We do it here — at the boundary
// where items enter the store — so every consumer (cards, lightbox, model
// showcase, …) renders the same colors for the same item without having to
// independently re-hash the id.
function decorateExploreItem(item) {
  if (!item || !item.id) return item;
  if (item.gradient && item.color && typeof item.gradientDark === 'boolean') return item;
  const meta = pickMusicGradientMeta(item.id);
  const gradient = item.gradient || meta.colors;
  return {
    ...item,
    gradient,
    // `gradientDark === true` means foreground text/icons should be dark
    // (the gradient is light enough that white would lose contrast). The
    // flag is hand-marked on each entry in MUSIC_GRADIENTS.
    gradientDark: typeof item.gradientDark === 'boolean' ? item.gradientDark : !!meta.dark,
    color: item.color || gradient[0],
  };
}

function decorateExploreItems(items) {
  if (!Array.isArray(items)) return items;
  return items.map(decorateExploreItem);
}

const useExploreStore = create((set, get) => ({
  items: [],
  itemsById: {},
  featuredItems: [],
  categories: [],
  /** `filters.mediaType ?? 'all'` for which `categories` was last loaded (avoids refetch on category-only nav). */
  categoriesFetchedMediaKey: null,
  categoriesDetailed: [],
  categoriesDetailedFetchedAt: 0,
  tags: [],
  models: [],
  selectedItem: null,

  // Per-model showcase cache: itemsByModelId[modelId] = array of explore items.
  // Populated by `fetchItemsByModel` and reused across the model chat page and
  // ModelDetailsModal so opening either is instant after the first fetch.
  itemsByModelId: {},
  itemsByModelFetchedAt: {},
  itemsByModelLoading: {},

  isLoading: false,
  isFeaturedLoading: false,
  isCategoriesDetailedLoading: false,
  errors: {
    feed: null,
    featured: null,
    categories: null,
    item: null,
    clone: null,
  },

  filters: {
    mediaType: null,
    category: null,
    sort: 'featured',
    search: '',
    model: null,
    style: null,
    tags: null,
    favorites: false,
  },

  pagination: {
    page: 1,
    pages: 1,
    total: 0,
    perPage: 30,
    hasMore: false,
  },

  actions: {
    fetchFeed: async (page = 1) => {
      try {
        set(state => ({
          isLoading: true,
          errors: { ...state.errors, feed: null },
        }));

        const { filters } = get();
        const { items: rawItems, pagination } = await exploreApi.fetchFeed(page, filters);
        const items = decorateExploreItems(rawItems);

        set(state => {
          const newItemsById = { ...state.itemsById };
          for (const item of items) {
            newItemsById[item.id] = item;
            if (item.slug) newItemsById[item.slug] = item;
          }

          return {
            items: page === 1 ? items : [...state.items, ...items],
            itemsById: newItemsById,
            pagination: {
              page: pagination.page,
              pages: pagination.pages,
              total: pagination.total,
              perPage: pagination.perPage,
              hasMore: pagination.hasMore,
            },
            isLoading: false,
          };
        });

        return items;
      } catch (error) {
        set(state => ({
          isLoading: false,
          errors: { ...state.errors, feed: error.message },
        }));
        return [];
      }
    },

    fetchFeatured: async () => {
      try {
        set(state => ({
          isFeaturedLoading: true,
          errors: { ...state.errors, featured: null },
        }));

        const { filters } = get();
        const { items: rawItems } = await exploreApi.fetchFeatured(filters.mediaType);
        const items = decorateExploreItems(rawItems);

        set(state => {
          const newItemsById = { ...state.itemsById };
          for (const item of items) {
            newItemsById[item.id] = item;
            if (item.slug) newItemsById[item.slug] = item;
          }
          return {
            featuredItems: items,
            itemsById: newItemsById,
            isFeaturedLoading: false,
          };
        });

        return items;
      } catch (error) {
        set(state => ({
          isFeaturedLoading: false,
          errors: { ...state.errors, featured: error.message },
        }));
        return [];
      }
    },

    fetchCategories: async () => {
      try {
        set(state => ({ errors: { ...state.errors, categories: null } }));

        const { filters } = get();
        const { categories } = await exploreApi.fetchCategories(filters.mediaType);
        const mediaKey = filters.mediaType ?? 'all';

        set({ categories, categoriesFetchedMediaKey: mediaKey });
        return categories;
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, categories: error.message },
        }));
        return [];
      }
    },

    // Detailed categories include featured item + counts for the categories
    // landing page. Cached aggressively because the payload is large and the
    // data changes slowly. Returns cached value immediately when fresh; when
    // stale or empty, refetches and updates state. Pass `force: true` to
    // bypass the cache (used by pull-to-refresh).
    fetchCategoriesDetailed: async ({ force = false, ttlMs = 5 * 60 * 1000 } = {}) => {
      const cached = get().categoriesDetailed;
      const fetchedAt = get().categoriesDetailedFetchedAt;
      const isFresh = fetchedAt && (Date.now() - fetchedAt) < ttlMs;

      if (!force && isFresh && cached.length) return cached;

      try {
        set(state => ({
          isCategoriesDetailedLoading: true,
          errors: { ...state.errors, categories: null },
        }));

        const { categories: rawCategories } = await exploreApi.fetchCategoriesDetailed();
        // Featured items embedded in categories should also receive their
        // gradient stamp so category cards render the same colors as the
        // feed/lightbox.
        const categories = Array.isArray(rawCategories)
          ? rawCategories.map(cat => ({
              ...cat,
              featuredItem: cat?.featuredItem ? decorateExploreItem(cat.featuredItem) : cat?.featuredItem,
              featuredItems: Array.isArray(cat?.featuredItems) ? decorateExploreItems(cat.featuredItems) : cat?.featuredItems,
            }))
          : rawCategories;

        set({
          categoriesDetailed: categories,
          categoriesDetailedFetchedAt: Date.now(),
          isCategoriesDetailedLoading: false,
        });
        return categories;
      } catch (error) {
        set(state => ({
          isCategoriesDetailedLoading: false,
          errors: { ...state.errors, categories: error.message },
        }));
        return get().categoriesDetailed;
      }
    },

    fetchTags: async () => {
      try {
        const { filters } = get();
        const { tags } = await exploreApi.fetchTags(filters.mediaType);
        set({ tags });
        return tags;
      } catch (error) {
        return [];
      }
    },

    fetchModels: async () => {
      try {
        const { filters } = get();
        const { models } = await exploreApi.fetchModels(filters.mediaType);
        set({ models });
        return models;
      } catch (error) {
        return [];
      }
    },

    // Returns explore items associated with a given model (by Mongo id), used
    // to render a delightful showcase on the model chat page and details modal.
    // Stale-while-revalidate: returns cached items immediately when fresh and
    // skips redundant in-flight fetches.
    fetchItemsByModel: async (modelId, { force = false, ttlMs = 5 * 60 * 1000 } = {}) => {
      if (!modelId) return [];
      const cached = get().itemsByModelId[modelId];
      const fetchedAt = get().itemsByModelFetchedAt[modelId];
      const isFresh = fetchedAt && (Date.now() - fetchedAt) < ttlMs;

      if (!force && isFresh && Array.isArray(cached)) return cached;
      if (get().itemsByModelLoading[modelId]) return cached || [];

      try {
        set(state => ({
          itemsByModelLoading: { ...state.itemsByModelLoading, [modelId]: true },
        }));

        const { items: rawItems } = await exploreApi.fetchItemsByModel(modelId);
        const items = decorateExploreItems(rawItems);

        set(state => {
          const newItemsById = { ...state.itemsById };
          for (const it of items) {
            newItemsById[it.id] = it;
            if (it.slug) newItemsById[it.slug] = it;
          }
          return {
            itemsById: newItemsById,
            itemsByModelId: { ...state.itemsByModelId, [modelId]: items },
            itemsByModelFetchedAt: { ...state.itemsByModelFetchedAt, [modelId]: Date.now() },
            itemsByModelLoading: { ...state.itemsByModelLoading, [modelId]: false },
          };
        });

        return items;
      } catch (error) {
        set(state => ({
          itemsByModelLoading: { ...state.itemsByModelLoading, [modelId]: false },
        }));
        return cached || [];
      }
    },

    fetchItem: async (idOrSlug) => {
      try {
        set(state => ({ errors: { ...state.errors, item: null } }));
        const { item: rawItem } = await exploreApi.fetchItem(idOrSlug);
        const item = decorateExploreItem(rawItem);

        set(state => {
          const newItemsById = { ...state.itemsById, [item.id]: item };
          if (item.slug) newItemsById[item.slug] = item;
          return {
            selectedItem: item,
            itemsById: newItemsById,
          };
        });

        return item;
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, item: error.message },
        }));
        return null;
      }
    },

    cloneItem: async (id) => {
      try {
        set(state => ({ errors: { ...state.errors, clone: null } }));
        const { clone } = await exploreApi.cloneItem(id);
        return clone;
      } catch (error) {
        set(state => ({
          errors: { ...state.errors, clone: error.message },
        }));
        return null;
      }
    },

    trackEvent: async (id, event) => {
      try {
        await exploreApi.trackEvent(id, event);
      } catch (_) {
        // non-critical, swallow
      }
    },

    setFilters: (newFilters) => {
      set(state => ({
        filters: { ...state.filters, ...newFilters },
      }));
    },

    applyFiltersAndRefetch: async (newFilters = {}, { forceFeatured = false } = {}) => {
      const { actions } = get();
      const prevFilters = get().filters;
      // Keep showing previous items until the new feed loads to avoid full list/header flash.
      set(state => ({
        filters: { ...state.filters, ...newFilters },
        errors: { ...state.errors, feed: null },
      }));
      const nextFilters = get().filters;
      const featuredItems = get().featuredItems;
      const needFeatured =
        forceFeatured ||
        prevFilters.mediaType !== nextFilters.mediaType ||
        !Array.isArray(featuredItems) ||
        featuredItems.length === 0;

      await Promise.all([
        actions.fetchFeed(1),
        needFeatured ? actions.fetchFeatured() : Promise.resolve(),
      ]);
    },

    clearSelectedItem: () => {
      set({ selectedItem: null });
    },

    resetExplore: () => {
      set({
        items: [],
        itemsById: {},
        featuredItems: [],
        selectedItem: null,
        isLoading: false,
        isFeaturedLoading: false,
        filters: {
          mediaType: null,
          category: null,
          sort: 'featured',
          search: '',
          model: null,
          style: null,
          tags: null,
          favorites: false,
        },
        categoriesFetchedMediaKey: null,
        pagination: { page: 1, pages: 1, total: 0, perPage: 30, hasMore: false },
        errors: { feed: null, featured: null, categories: null, item: null, clone: null },
      });
    },
  },
}));

export { useExploreStore };
export default useExploreStore;
